import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, ArrowRight, Coffee, Trophy, Gamepad2, Briefcase, Users, PartyPopper, Wrench, Home, Building2, Calendar, Settings, Cpu, LifeBuoy } from 'lucide-react';
import { useSeo } from '@/lib/seo';
import { usePageMedia } from '@/lib/usePageMedia';

const VENUE_SERVICES = [
  { icon: Gamepad2, label: 'Casual sim racing sessions' },
  { icon: Trophy, label: 'Sim racing leagues and championships' },
  { icon: Users, label: 'Competitive esports events and tournaments' },
  { icon: LifeBuoy, label: 'Professional sim racing coaching and driver development' },
  { icon: Briefcase, label: 'Corporate events and team-building experiences' },
  { icon: PartyPopper, label: 'Private group bookings and parties' },
];

const SALES_SERVICES = [
  { icon: Settings, label: 'Sim racing rig sales and consultation' },
  { icon: Cpu, label: 'Custom simulator design and specification' },
  { icon: Home, label: 'Home simulator installations' },
  { icon: Building2, label: 'Commercial simulator installations' },
  { icon: Calendar, label: 'Event and exhibition simulator builds' },
  { icon: Wrench, label: 'On-site assembly and commissioning' },
  { icon: Gamepad2, label: 'Motion simulator solutions' },
  { icon: LifeBuoy, label: 'Ongoing support, maintenance and upgrades' },
];

export default function About() {
  const { get } = usePageMedia();
  const heroImg = get('about', 'hero');
  useSeo({
    title: 'About Motion Reality Sim Racing — Premium Sim Racing Experiences & Solutions',
    description: 'Motion Reality Sim Racing is a UK-based provider of premium sim racing experiences, professional simulator solutions, and high-performance racing equipment for enthusiasts, motorsport fans, esports competitors and professional drivers.',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: 'About Motion Reality Sim Racing',
      url: 'https://motionreality.co.uk/about',
      mainEntity: {
        '@type': 'Organization',
        name: 'Motion Reality Sim Racing',
        slogan: 'Bridging the gap between virtual and real-world motorsport.',
      },
    },
  });
  return (
    <div className="bg-[#0d0d0f] min-h-screen pt-16">

      {/* Header */}
      <section className="relative py-24 px-6 lg:px-10 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent" />
        {heroImg && (
          <>
            <img src={heroImg} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
            <div className="absolute inset-0 bg-gradient-to-b from-[#0d0d0f]/70 to-[#0d0d0f]" />
          </>
        )}
        <div className="max-w-5xl mx-auto relative">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <p className="text-primary text-xs font-bold tracking-widest mb-3">WHO WE ARE</p>
            <h1 className="text-white font-black text-4xl md:text-6xl uppercase leading-tight mb-6">
              BRIDGING THE GAP<br /><span className="text-primary">BETWEEN VIRTUAL & REAL.</span>
            </h1>
            <div className="space-y-4 text-white/55 max-w-2xl text-sm leading-relaxed">
              <p>
                Motion Reality Sim Racing is a UK-based provider of premium sim racing experiences, professional simulator solutions, and high-performance racing equipment for enthusiasts, motorsport fans, esports competitors, and professional drivers.
              </p>
              <p>
                Built on a foundation of precision engineering, innovation, and a passion for motorsport, we are dedicated to delivering authentic racing experiences that combine cutting-edge technology with exceptional realism. Our focus is simple: to bridge the gap between virtual and real-world motorsport through immersive, performance-driven simulator solutions.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Ultimate Experience */}
      <section className="py-20 px-6 lg:px-10 border-t border-white/5">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <p className="text-primary text-xs font-bold tracking-widest mb-3">OUR VENUE</p>
            <h2 className="text-white font-black text-2xl md:text-3xl uppercase mb-6 leading-tight">
              The Ultimate Sim Racing Experience
            </h2>
            <div className="space-y-4 text-white/55 text-sm leading-relaxed">
              <p>
                Our facility offers a fully equipped sim racing environment designed to cater to drivers of all abilities. Whether you are experiencing sim racing for the first time, competing at an advanced level, or using simulation as a professional training tool, we provide an experience tailored to your needs.
              </p>
              <p className="flex items-start gap-3">
                <Coffee className="w-4 h-4 text-primary mt-1 shrink-0" />
                <span>Visitors can enjoy a welcoming atmosphere complete with a snack bar and a range of non-alcoholic refreshments while experiencing some of the most advanced simulator equipment available.</span>
              </p>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="bg-[#111115] border border-white/5 p-8">
            <p className="text-primary text-xs font-bold tracking-widest mb-2">OUR SERVICES INCLUDE</p>
            <div className="space-y-4 mt-6">
              {VENUE_SERVICES.map((s) => (
                <div key={s.label} className="flex items-start gap-3">
                  <s.icon className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                  <p className="text-white/65 text-sm">{s.label}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Premium Rigs */}
      <section className="py-20 px-6 lg:px-10 bg-[#111115] border-t border-white/5">
        <div className="max-w-5xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-12">
            <p className="text-primary text-xs font-bold tracking-widest mb-3">SHOWCASE</p>
            <h2 className="text-white font-black text-2xl md:text-3xl uppercase mb-4 leading-tight">
              Premium Simulator Rigs
            </h2>
            <p className="text-white/55 text-sm leading-relaxed max-w-2xl">
              Motion Reality Sim Racing features a range of simulator solutions designed to suit every level of driver. Our flagship installations include:
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
            {['Custom AMG Simulator Rig', 'Custom Porsche Simulator Rig'].map((rig, i) => (
              <motion.div
                key={rig}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-[#0d0d0f] border border-white/5 p-8 group hover:border-primary/30 transition-colors"
              >
                <div className="w-8 h-[3px] bg-primary mb-5" />
                <h3 className="text-white font-bold text-lg mb-1">{rig}</h3>
              </motion.div>
            ))}
          </div>

          <p className="text-white/55 text-sm leading-relaxed max-w-2xl mb-6">
            Alongside these showcase simulators, we offer a selection of consumer and enthusiast-grade racing rigs available for:
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5">
            {['Testing and demonstrations', 'Short and long-term hire', 'Driver training and development', 'Product evaluations'].map((item, i) => (
              <motion.div
                key={item}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.07 }}
                className="bg-[#111115] p-6"
              >
                <CheckCircle className="w-4 h-4 text-primary mb-3" />
                <p className="text-white/60 text-sm leading-relaxed">{item}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Sales, Installation & Bespoke */}
      <section className="py-20 px-6 lg:px-10 border-t border-white/5">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <p className="text-primary text-xs font-bold tracking-widest mb-3">BEYOND THE VENUE</p>
            <h2 className="text-white font-black text-2xl md:text-3xl uppercase mb-6 leading-tight">
              Sales, Installation &amp; Bespoke Solutions
            </h2>
            <div className="space-y-4 text-white/55 text-sm leading-relaxed">
              <p>
                Beyond our venue experience, Motion Reality Sim Racing specialises in the supply, design, installation, and support of complete simulator solutions.
              </p>
              <p>
                Whether you are looking for an entry-level racing setup, a professional-grade simulator, or a fully bespoke motion platform, our team provides expert guidance from initial consultation through to final installation.
              </p>
              <p>
                Every installation is tailored to the client's requirements, ensuring the ideal balance of performance, realism, reliability and value.
              </p>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="bg-[#111115] border border-white/5 p-8">
            <p className="text-primary text-xs font-bold tracking-widest mb-2">OUR SERVICES INCLUDE</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 mt-6">
              {SALES_SERVICES.map((s) => (
                <div key={s.label} className="flex items-start gap-3">
                  <s.icon className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                  <p className="text-white/65 text-sm">{s.label}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-24 px-6 lg:px-10 bg-[#111115] border-t border-white/5">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <p className="text-primary text-xs font-bold tracking-widest mb-3">OUR MISSION</p>
            <h2 className="text-white font-black text-3xl md:text-4xl uppercase mb-6 leading-tight">
              OUR MISSION
            </h2>
            <p className="text-white/55 text-sm leading-relaxed mb-4">
              At Motion Reality Sim Racing, we are committed to delivering exceptional sim racing experiences, industry-leading simulator solutions, and expert support to drivers and businesses throughout the UK.
            </p>
            <p className="text-white/55 text-sm leading-relaxed">
              By combining advanced technology, precision engineering and motorsport expertise, we create immersive racing.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 lg:px-10">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-primary text-xs font-bold tracking-widest mb-3">START TODAY</p>
          <h2 className="text-white font-black text-3xl md:text-4xl uppercase mb-6">READY TO RACE?</h2>
          <p className="text-white/40 text-sm mb-8">Book a session, visit our shop, or talk to us about a bespoke build.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/shop">
              <button className="bg-primary hover:bg-primary/90 text-white font-bold px-8 py-3.5 text-sm transition-colors flex items-center gap-2">
                SHOP NOW <ArrowRight className="w-4 h-4" />
              </button>
            </Link>
            <Link to="/bookings">
              <button className="border border-white/20 text-white hover:bg-white/5 font-semibold px-8 py-3.5 text-sm transition-colors">
                BOOK A SESSION
              </button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}